import type { FsError, PlatformBridge } from '@slate/core';
import { basename, isUnderRoot, isValidName, joinPath } from './containment';
import { useFsUndoStore, type FsUndoEntry } from '../store/fsUndoStore';

/**
 * High-level FS actions for the sidebar file-manager. Each function wraps
 * one or more `platform.fs.*` calls with the UX glue the bridge intentionally
 * doesn't supply:
 *   - workspace-root containment guard (an FS op that would escape the root
 *     is rejected before invoking the bridge),
 *   - name validation,
 *   - duplicate / new-file slot picking (next available `name copy.md`,
 *     `Untitled 2.md`, etc.),
 *   - structured FsError → human-readable message mapping,
 *   - a one-shot toast emit (success and failure).
 *
 * Callers (the FileTree) refresh the affected folder themselves after the
 * action resolves — fsActions stays a thin domain-action layer and doesn't
 * touch React state directly.
 */

export interface FsActionsContext {
  platform: PlatformBridge;
  /** The workspace root the affected node lives under. Containment is checked against this. */
  workspaceRoot: string;
  /** Emit a transient toast to the user. Success and failure both go here. */
  toast: (message: string, opts?: { kind?: 'info' | 'error' }) => void;
}

/**
 * Format an `FsError` (or unknown rejection) as a short user-facing sentence.
 * The bridge guarantees `FsError` shape for `platform.fs.*` rejections, but
 * an aborted/internal rejection might surface as a generic Error — we
 * defensively handle both.
 */
export function describeFsError(err: unknown, opName: string): string {
  if (typeof err === 'object' && err !== null && 'kind' in err) {
    const fsErr = err as FsError;
    switch (fsErr.kind) {
      case 'NotFound':
        return `${opName} failed: the file or folder no longer exists.`;
      case 'AlreadyExists':
        return `${opName} failed: a file or folder with that name already exists.`;
      case 'PermissionDenied':
        return `${opName} failed: permission denied.`;
      case 'InvalidPath':
        return `${opName} failed: invalid path${fsErr.message ? ` (${fsErr.message})` : ''}.`;
      case 'Io':
      default:
        return `${opName} failed${fsErr.message ? `: ${fsErr.message}` : '.'}`;
    }
  }
  return `${opName} failed.`;
}

/**
 * Reject `name` with a friendly toast and return false if it isn't a valid
 * single-segment filename. Returns true when the name is OK to proceed.
 */
function validateName(ctx: FsActionsContext, name: string, opName: string): boolean {
  if (!isValidName(name)) {
    ctx.toast(`${opName} failed: invalid name "${name}".`, { kind: 'error' });
    return false;
  }
  return true;
}

/** Reject and toast when `target` would escape `ctx.workspaceRoot`. */
function checkContainment(ctx: FsActionsContext, target: string, opName: string): boolean {
  if (!isUnderRoot(target, ctx.workspaceRoot)) {
    ctx.toast(`${opName} blocked: target is outside the workspace.`, { kind: 'error' });
    return false;
  }
  return true;
}

/** Create a new (empty) text file in `parentDir`. Returns its path, or null on failure. */
export async function createFileIn(
  ctx: FsActionsContext,
  parentDir: string,
  name: string,
): Promise<string | null> {
  if (!validateName(ctx, name, 'Create file')) return null;
  const target = joinPath(parentDir, name);
  if (!checkContainment(ctx, target, 'Create file')) return null;
  if (!ctx.platform.fs) {
    ctx.toast('Create file failed: filesystem not available on this platform.', { kind: 'error' });
    return null;
  }
  try {
    await ctx.platform.fs.createFile(target, '', { overwrite: false });
    ctx.toast(`Created "${name}".`);
    return target;
  } catch (err) {
    ctx.toast(describeFsError(err, 'Create file'), { kind: 'error' });
    return null;
  }
}

/** Create a new folder in `parentDir`. Returns its path, or null on failure. */
export async function createFolderIn(
  ctx: FsActionsContext,
  parentDir: string,
  name: string,
): Promise<string | null> {
  if (!validateName(ctx, name, 'Create folder')) return null;
  const target = joinPath(parentDir, name);
  if (!checkContainment(ctx, target, 'Create folder')) return null;
  if (!ctx.platform.fs) {
    ctx.toast('Create folder failed: filesystem not available on this platform.', { kind: 'error' });
    return null;
  }
  try {
    await ctx.platform.fs.mkdir(target, { recursive: false });
    ctx.toast(`Created "${name}/".`);
    return target;
  } catch (err) {
    ctx.toast(describeFsError(err, 'Create folder'), { kind: 'error' });
    return null;
  }
}

/**
 * Rename `oldPath` to a new name within the same parent. Returns the new
 * absolute path, or null on validation/operation failure.
 */
export async function renameNode(
  ctx: FsActionsContext,
  oldPath: string,
  newName: string,
): Promise<string | null> {
  if (!validateName(ctx, newName, 'Rename')) return null;
  const oldName = basename(oldPath);
  if (oldName === newName) return oldPath; // no-op: avoid an FS roundtrip.
  // Reconstruct the parent dir from the old path and re-join with the new
  // name. We use containment.dirOf rather than path.dirname so the result
  // matches the separator convention of the input.
  const parent = oldPath.replace(/[\\/][^\\/]*$/, '');
  const target = joinPath(parent, newName);
  if (!checkContainment(ctx, target, 'Rename')) return null;
  if (!ctx.platform.fs) {
    ctx.toast('Rename failed: filesystem not available on this platform.', { kind: 'error' });
    return null;
  }
  try {
    await ctx.platform.fs.rename(oldPath, target, { overwrite: false });
    ctx.toast(`Renamed "${oldName}" → "${newName}".`);
    return target;
  } catch (err) {
    ctx.toast(describeFsError(err, 'Rename'), { kind: 'error' });
    return null;
  }
}

/**
 * Pick the next available "copy" name in `siblings`. Mirrors macOS Finder:
 *   `note.md`        → `note copy.md`
 *   `note copy.md`   → `note copy 2.md`
 *   `note copy 2.md` → `note copy 3.md`
 *   `dir`            → `dir copy`            (folders: no extension games)
 *
 * Returns the chosen basename (not a full path).
 */
export function pickDuplicateName(originalName: string, siblings: ReadonlySet<string>): string {
  // Split into stem + extension (no extension for names without a dot or for
  // names that *start* with a dot — e.g. `.env` has no "extension" in this
  // sense). Folders flow through this path too — they typically have no
  // extension, which is fine.
  const dot = originalName.lastIndexOf('.');
  const hasExt = dot > 0; // > 0, not >= 0, so `.env` keeps its dot.
  const stem = hasExt ? originalName.slice(0, dot) : originalName;
  const ext = hasExt ? originalName.slice(dot) : '';

  // Already a "copy" name? Match `<base> copy` or `<base> copy <n>`.
  const m = stem.match(/^(.*) copy(?: (\d+))?$/);
  const base = m ? m[1] : stem;
  let n = m ? (m[2] ? parseInt(m[2], 10) + 1 : 2) : 1;

  while (true) {
    const candidate = n === 1 ? `${base} copy${ext}` : `${base} copy ${n}${ext}`;
    if (!siblings.has(candidate)) return candidate;
    n += 1;
    // Safety bound: any sibling set with this many "copy" entries is pathological.
    if (n > 9999) return `${base} copy ${Date.now()}${ext}`;
  }
}

/**
 * Duplicate `sourcePath` next to itself. `siblings` is the set of basenames
 * currently in the parent directory — the caller already has it from the
 * tree's children cache, so we don't `readDir` again. Returns the new path or
 * null.
 */
export async function duplicateNode(
  ctx: FsActionsContext,
  sourcePath: string,
  siblings: ReadonlySet<string>,
): Promise<string | null> {
  const name = basename(sourcePath);
  const newName = pickDuplicateName(name, siblings);
  const parent = sourcePath.replace(/[\\/][^\\/]*$/, '');
  const target = joinPath(parent, newName);
  if (!checkContainment(ctx, target, 'Duplicate')) return null;
  if (!ctx.platform.fs) {
    ctx.toast('Duplicate failed: filesystem not available on this platform.', { kind: 'error' });
    return null;
  }
  try {
    await ctx.platform.fs.copy(sourcePath, target, { overwrite: false });
    ctx.toast(`Duplicated "${name}" → "${newName}".`);
    return target;
  } catch (err) {
    ctx.toast(describeFsError(err, 'Duplicate'), { kind: 'error' });
    return null;
  }
}

/**
 * Move a path to the OS trash. Returns true on success. Containment is
 * checked so the UI can't be coerced into trashing a path outside the
 * workspace root (e.g. via a stale row whose `path` was edited).
 */
export async function trashNode(ctx: FsActionsContext, path: string): Promise<boolean> {
  return trashNodes(ctx, [path]);
}

/**
 * Batch move-to-trash. Either every path lands in the trash or the entire
 * batch fails (the Rust command aborts on the first error). Returns true on
 * success.
 */
export async function trashNodes(ctx: FsActionsContext, paths: readonly string[]): Promise<boolean> {
  if (paths.length === 0) return true;
  for (const p of paths) if (!checkContainment(ctx, p, 'Move to Trash')) return false;
  if (!ctx.platform.fs) {
    ctx.toast('Move to Trash failed: filesystem not available on this platform.', {
      kind: 'error',
    });
    return false;
  }
  try {
    await ctx.platform.fs.trash([...paths]);
    const msg =
      paths.length === 1
        ? `Moved "${basename(paths[0])}" to Trash.`
        : `Moved ${paths.length} items to Trash.`;
    ctx.toast(msg);
    return true;
  } catch (err) {
    ctx.toast(describeFsError(err, 'Move to Trash'), { kind: 'error' });
    return false;
  }
}

/**
 * Permanently delete each path (no trash). Used by Shift-Delete. Callers MUST
 * have prompted the user with a stronger "this can't be undone" confirmation;
 * fsActions itself doesn't gate on confirmation — that's UI concern.
 */
export async function removeNodes(ctx: FsActionsContext, paths: readonly string[]): Promise<boolean> {
  if (paths.length === 0) return true;
  for (const p of paths) if (!checkContainment(ctx, p, 'Delete')) return false;
  if (!ctx.platform.fs) {
    ctx.toast('Delete failed: filesystem not available on this platform.', { kind: 'error' });
    return false;
  }
  try {
    await ctx.platform.fs.remove([...paths]);
    const msg =
      paths.length === 1
        ? `Deleted "${basename(paths[0])}" permanently.`
        : `Deleted ${paths.length} items permanently.`;
    ctx.toast(msg);
    return true;
  } catch (err) {
    ctx.toast(describeFsError(err, 'Delete'), { kind: 'error' });
    return false;
  }
}

/** Per-conflict resolution choice returned by the UI prompt. */
export type PasteConflictAction = 'replace' | 'keep-both' | 'skip' | 'cancel';
export interface PasteConflictChoice {
  action: PasteConflictAction;
  /** When true, the chosen action applies to every subsequent conflict in this batch. */
  applyToAll?: boolean;
}
export interface PasteConflictPrompt {
  sourcePath: string;
  targetPath: string;
  /** True when more conflicts may follow this one — used to enable "Apply to all". */
  hasMore: boolean;
}
export type PasteConflictResolver = (prompt: PasteConflictPrompt) => Promise<PasteConflictChoice>;

/** A single successful paste-step that should be recorded in the undo journal. */
export interface PasteJournalEntry {
  mode: 'cut' | 'copy';
  source: string;
  target: string;
}

/**
 * Paste `sources` into `targetDir` as the given mode. Cut = move, Copy =
 * recursive copy. Conflicts (target basename already exists in
 * `targetSiblings`) go through `resolveConflict`, which can respond with
 * Replace, Keep both, Skip, or Cancel-the-whole-batch. The caller renders the
 * actual prompt; this function stays headless.
 *
 * Returns the list of *successful* paste-steps so the caller can push them
 * into the undo journal. Containment is enforced on every target so a paste
 * can't escape the workspace root, even via a deeply nested source.
 */
export async function pasteSources(
  ctx: FsActionsContext,
  sources: readonly string[],
  targetDir: string,
  mode: 'cut' | 'copy',
  resolveConflict: PasteConflictResolver,
  targetSiblings: ReadonlySet<string>,
): Promise<PasteJournalEntry[]> {
  if (!checkContainment(ctx, targetDir, 'Paste')) return [];
  if (!ctx.platform.fs) {
    ctx.toast('Paste failed: filesystem not available on this platform.', { kind: 'error' });
    return [];
  }
  const fs = ctx.platform.fs;
  const successes: PasteJournalEntry[] = [];
  const localSiblings = new Set(targetSiblings);
  let bulkAction: PasteConflictAction | null = null;
  let failures = 0;

  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    const name = basename(source);
    let targetPath = joinPath(targetDir, name);

    // Containment: a deeply nested source basenamed back into the root is fine
    // because `targetPath` is built from `targetDir + name` (one join level),
    // but we re-check explicitly to be defensive.
    if (!isUnderRoot(targetPath, ctx.workspaceRoot)) {
      ctx.toast(`Skipped "${name}": target outside the workspace.`, { kind: 'error' });
      failures++;
      continue;
    }

    // Self / subtree guards. Moving or copying a folder into itself (or into
    // its own descendant) is undefined behaviour at the OS level — skip with
    // a friendly toast rather than letting fs.{move,copy} explode.
    if (targetPath === source || source === targetDir) {
      ctx.toast(`Skipped "${name}": same source and destination.`, { kind: 'info' });
      continue;
    }
    if (targetDir === source || targetDir.startsWith(`${source}/`)) {
      ctx.toast(`Skipped "${name}": can't paste a folder into itself.`, { kind: 'error' });
      failures++;
      continue;
    }

    // Conflict resolution.
    let action: 'proceed' | PasteConflictAction = 'proceed';
    if (localSiblings.has(name)) {
      if (bulkAction != null) {
        action = bulkAction;
      } else {
        const remaining = sources.slice(i + 1).filter((s) => localSiblings.has(basename(s))).length;
        const choice = await resolveConflict({
          sourcePath: source,
          targetPath,
          hasMore: remaining > 0,
        });
        action = choice.action;
        if (choice.applyToAll && action !== 'cancel') bulkAction = action;
      }
      if (action === 'cancel') break;
      if (action === 'skip') continue;
      if (action === 'keep-both') {
        targetPath = joinPath(targetDir, pickDuplicateName(name, localSiblings));
      }
    }

    const overwrite = action === 'replace';
    try {
      if (mode === 'cut') {
        await fs.move(source, targetPath, { overwrite });
      } else {
        await fs.copy(source, targetPath, { overwrite });
      }
      successes.push({ mode, source, target: targetPath });
      localSiblings.add(basename(targetPath));
    } catch (err) {
      ctx.toast(describeFsError(err, mode === 'cut' ? 'Move' : 'Copy'), { kind: 'error' });
      failures++;
    }
  }

  if (successes.length > 0) {
    const verb = mode === 'cut' ? 'Moved' : 'Copied';
    const tail = failures > 0 ? ` (${failures} failed)` : '';
    ctx.toast(
      `${verb} ${successes.length} item${successes.length === 1 ? '' : 's'} into "${basename(targetDir)}".${tail}`,
    );
  }
  return successes;
}

/**
 * Pick the next available "Untitled" name in `siblings` for a fresh inline-
 * create. The placeholder is what the inline-create input is pre-filled with;
 * the user can replace it before pressing Enter.
 */
export function pickUntitledName(
  kind: 'file' | 'folder',
  siblings: ReadonlySet<string>,
): string {
  const base = kind === 'file' ? 'Untitled' : 'New Folder';
  const ext = kind === 'file' ? '.md' : '';
  const first = `${base}${ext}`;
  if (!siblings.has(first)) return first;
  for (let n = 2; n <= 9999; n++) {
    const candidate = `${base} ${n}${ext}`;
    if (!siblings.has(candidate)) return candidate;
  }
  return `${base} ${Date.now()}${ext}`;
}

/**
 * Compute the absolute parent directories that an undo of `entry` will touch,
 * so the caller knows which dirs to re-readDir after the op completes. Order
 * doesn't matter — the caller refreshes each.
 */
export function undoAffectedParents(entry: FsUndoEntry): string[] {
  const dirOf = (p: string): string => p.replace(/[\\/][^\\/]*$/, '');
  switch (entry.kind) {
    case 'rename':
      return [dirOf(entry.from)]; // same parent on both sides
    case 'create-file':
    case 'create-folder':
      return [dirOf(entry.path)];
    case 'duplicate':
    case 'copy':
      return [dirOf(entry.copyPath)];
    case 'move':
      return [dirOf(entry.from), dirOf(entry.to)];
  }
}

/**
 * Pop the most recent journal entry and replay its inverse. Returns the
 * popped entry (so the caller can refresh affected parents) on success, or
 * null when the journal is empty or the op failed.
 */
export async function undoLast(ctx: FsActionsContext): Promise<FsUndoEntry | null> {
  const entry = useFsUndoStore.getState().popLast();
  if (!entry) {
    ctx.toast('Nothing to undo.');
    return null;
  }
  if (!ctx.platform.fs) {
    ctx.toast('Undo failed: filesystem not available on this platform.', { kind: 'error' });
    return null;
  }
  const fs = ctx.platform.fs;
  try {
    switch (entry.kind) {
      case 'rename':
        // Inverse rename. Containment check on the (restored) target.
        if (!checkContainment(ctx, entry.from, 'Undo rename')) return null;
        await fs.rename(entry.to, entry.from, { overwrite: false });
        ctx.toast(`Renamed "${basename(entry.to)}" back to "${basename(entry.from)}".`);
        break;
      case 'create-file':
      case 'create-folder':
      case 'duplicate':
      case 'copy': {
        const target =
          entry.kind === 'create-file' || entry.kind === 'create-folder'
            ? entry.path
            : entry.copyPath;
        if (!checkContainment(ctx, target, 'Undo')) return null;
        // Inverse-of-create / inverse-of-copy = send the artifact to Trash.
        // We deliberately route through trash (not remove) so the user can
        // still get their content back via the OS if the undo itself was a
        // mistake.
        await fs.trash([target]);
        ctx.toast(`Undone — "${basename(target)}" moved to Trash.`);
        break;
      }
      case 'move':
        if (!checkContainment(ctx, entry.from, 'Undo move')) return null;
        await fs.move(entry.to, entry.from, { overwrite: false });
        ctx.toast(`Moved "${basename(entry.to)}" back.`);
        break;
    }
    return entry;
  } catch (err) {
    ctx.toast(describeFsError(err, 'Undo'), { kind: 'error' });
    return null;
  }
}
